import {
  DEFAULT_AVAILABLE_GOAL_CATEGORIES,
  DEFAULT_CONFIRM_ACTION_MODAL,
  DEFAULT_CURRENT_ACTIVITY_PROPS,
  DEFAULT_FILTER_DATA,
  DEFAULT_LOCAL_DEVICE_SETTINGS,
  DEFAULT_MORE_OPTION_ACTIVITY,
  DEFAULT_NEW_TASK,
  DEFAULT_ONBOARDING_STATS,
  DEFAULT_ROUTINE_SUGGESTION_PAYLOAD,
  DEFAULT_ROUTINE_TIME_VALIDATION,
  DEFAULT_SYNCED_EXTERNAL_PLATFORMS,
  DEFAULT_TEAM_MEMBERS,
  DEFAULT_TO_DO_SUBTASKS,
  DEFAULT_USER_DETAILS,
  INITIAL_SETTINGS
} from 'assets/data';
import {
  ActivityTableFilter,
  AiToneOptions,
  ChatBotState,
  ChatRoles,
  FEED_BACK_QUESTION,
  FOCUS_SUMMARY_MODAL_MODE,
  FONT_OPTION,
  FetchUsersOrderByOptions,
  Mode,
  MORE_OPTION_REVAMP_TAB,
  ProjectDetailTab,
  ProjectDetailsViewMode,
  ProjectLifecycleStatus,
  ProjectsListViewMode,
  SORT_MODE,
  DayPlanTodaySegment,
  THEME_OPTION,
  TO_DO_STATUS,
  IntegrationTab
} from 'enums';
import {
  DEFAULT_TAB_LAYOUT_TAB,
  DEFAULT_TO_DO_PLAYER,
  EMPTY_STRING,
  LANGUAGE,
  MARKET_TAB_INDEX,
  PAGE,
  PLATFORMS,
  ROUTINE_PLAYER_FEATURE,
  SEARCH_RESULT,
  STATS_TAB_INDICES,
  TABS,
  TO_DO_TAB
} from 'constants/general';
import { TEAM_COLUMN_TITLES } from 'constants/table';
import {
  AccountabilityBuddySlice,
  AdminSlice,
  AdminTeamsSlice,
  CacheSlice,
  ChatBotSlice,
  ClassManagementSlice,
  ClassSessionSlice,
  CourseSlice,
  FilterSlice,
  FocusModeSlice,
  GeneralSlice,
  HabitPackSlice,
  InquirySlice,
  McpTokenSlice,
  ModalSlice,
  PaginationSlice,
  PlatformIntegrationSlice,
  ProcrastinateSlice,
  RoutineSuggestionsSlice,
  SettingSlice,
  SubscriptionSlice,
  TeamSlice,
  ToDoSlice,
  TodoStatusPagination,
  TodoTasksByStatus,
  UserSlice
} from 'interfaces/initial-states.interface';
import { NotesState } from 'interfaces/note.interface';
import { ProjectNoteSlice } from 'interfaces/project-note.interface';
import { PortfolioSlice, ProjectSlice } from 'interfaces/project.interface';
import { DayPlanSlice } from 'interfaces/day-plan.interface';
import { StatsState } from 'interfaces/stats.interface';
import { formatDateToYMD } from 'utils/time.util';
import { t } from 'services/i18n.service';
import { isRouteEmbedded } from 'utils/routes.util';

export const initialUserState: UserSlice = {
  details: DEFAULT_USER_DETAILS,
  focus_modes: [],
  morningCompletionPercent: 0,
  eveningCompletionPercent: 0,
  weeklySummaryStats: [],
  isMorningStatsUpdating: false,
  isEveningStatsUpdating: false,
  isWeeklySummaryUpdating: false,
  isUserAdmin: false,
  isCurrentActivityPropsUpdating: true,
  currentActivityProps: DEFAULT_CURRENT_ACTIVITY_PROPS,
  last_completed_activity_log: null,
  isMorningRoutineCompleted: false,
  isEveningRoutineCompleted: false,
  isBreakingRoutineCompleted: false,
  completed_routines: [],
  onboardingStats: DEFAULT_ONBOARDING_STATS,
  areOnboardingStatsLoading: false,
  areUserDetailsLoading: false,
  motivationalSummary: EMPTY_STRING,
  isMotivationalSummaryLoading: false,
  isMotivationalSummaryStopGenerating: true,
  isUpdatingUserPrivacyPolicy: false,
  isDailySummaryFetching: false,
  dailySummary: {
    focusSummary: [],
    daySummaryAVG: [],
    daySummarySUM: [],
    daySummaryDuration: []
  },
  isLogTermGoalUpdating: false,
  isTrialUser: false,
  isWeeklyFocusBlockSummaryUpdating: false,
  weeklyFocusBlockSummary: [],
  isUsernameUpdating: false,
  isUserLoggingOut: false,
  isWeeklyStatsUpdating: true,
  openAIPrompt: EMPTY_STRING,
  isSavingUserFeedback: false,
  showThankYouModal: false,
  showTeamNameModal: false,
  isUpdatingUserProfileDescription: false,
  isUpdatingUserProfileImage: false,
  isUpdatingUserTimezone: false,
  isFetchingUserTimezone: true,
  isCancelingSubscription: false,
  isFetchingLogTermGoal: false,
  synced_external_platforms: DEFAULT_SYNCED_EXTERNAL_PLATFORMS,
  isFetchingSyncedExternalPlatforms: false,
  isSendingEmailVerification: false,
  isFetchingUserFocusModes: false,
  isFetchingUserDetailsSummary: false,
  isCompletedFocusBlocksFetching: false,
  completedFocusBlocks: []
};

export const initialSettingState: SettingSlice = {
  currentSettings: INITIAL_SETTINGS,
  previousSettings: INITIAL_SETTINGS,
  activityLibrary: [],
  previousActivityLibrary: [],
  isSettingLoading: false,
  isSettingLoaded: false,
  mode: Mode.DEFAULT,
  platform: PLATFORMS.WEB,
  suggestions: [],
  tabs: TABS.DEFAULT,
  recentHabitIcons: [],
  customRoutine: EMPTY_STRING,
  error: {
    message: ''
  },
  isYouTubeURLMetadataFetching: true,
  isShowMoreOptionsOpened: false,
  isShowLibraryMoreOptionsOpened: false,
  moreOptions: {
    position: -1,
    type: ''
  },
  isTokenAddedToInterceptor: false,
  areLibraryActivitiesLoading: false,
  isRoutineSummaryFetching: false,
  currentPlayingActivity: null,
  nextPlayingActivity: null,
  routinePlayerActivities: { type: null, activities: [] },
  routineSummary: [],
  isDeviceRegistering: false,
  isSavingRoutineCompletedActivity: false,
  isShowRoutineModal: false,
  isFetchingNextRoutineActivity: false,
  isTextEditorWithinCharacterLimit: true,
  activeTabIndex: 0,
  isSettingEditingFinished: false,
  areSettingsValid: true,
  activeMarketTab: MARKET_TAB_INDEX.HABIT_PACKS,
  areSettingsSaving: false,
  language: LANGUAGE.GB,
  isUnlockHabitPackModalShown: false,
  habitPackUnlockTarget: null,
  showPrivacyPolicyModal: false,
  showSkipActivityModal: false,
  routinePlayerStep: ROUTINE_PLAYER_FEATURE.ACTIVITY_CATEGORIES,
  leaders: {
    user_rank: null,
    users_rankings: []
  },
  isLeaderBoardFetching: true,
  isRequestDataConfirmationModalShown: false,
  isRequestDataProcessing: false,
  isAccountDeleteConfirmationModalShown: false,
  isAccountDeleting: false,
  isAccountDeleted: false,
  isUserSettingsModified: false,
  themeMode: THEME_OPTION.LIGHT,
  font: FONT_OPTION.SANS,
  isNewHabitAppended: false,
  isActivityLibraryUpdating: false,
  flags: [],
  isPlatformCoursesSyncing: false,
  validationErrors: [],
  feedback: {
    question: '',
    metadata: {},
    type: FEED_BACK_QUESTION.TEXT_AND_RATING
  },
  isUserOnboarding: false,
  routineTimeValidation: DEFAULT_ROUTINE_TIME_VALIDATION,
  aiTone:
    Object.values(AiToneOptions)[
      Math.floor(Math.random() * Object.values(AiToneOptions).length)
    ],
  isSearchingDevice: false,
  isNewCustomRoutineAppended: false,
  statsPageActiveTabIndex: isRouteEmbedded
    ? STATS_TAB_INDICES.STREAK
    : STATS_TAB_INDICES.STREAK - 1,
  isNewUserSigninProcess: false,
  isFetchingDeviceSettings: false,
  isUpdatingDeviceSettings: false,
  localDeviceSettings: DEFAULT_LOCAL_DEVICE_SETTINGS,
  isShowPrivacyPolicyModal: false,
  highlightShutdownTime: false,
  highlightCutoffTime: false
};

export const initialHabitPackState: HabitPackSlice = {
  areHabitPacksLoading: false,
  marketplaceApprovedPacks: [],
  installedHabitPacks: [],
  userCreatedHabitPacks: [],
  featuredHabitPacks: [],
  isOnHabitPacksTab: false,
  currentViewedPack: null,
  sharedViewedPack: null,
  isCurrentHabitPackFetching: false,
  isSharedHabitPackFetching: true,
  isUserInstalledPacksFetching: true, // it will prevent a blink effect of the content until this value become true
  installingPack: false,
  uninstallingPack: false,
  isUserUpsertingPack: false,
  isHabitPackAllowedToBeEdited: false,
  isHabitPackPdfShowed: false,
  initialCurrentViewedPack: null,
  isHabitPacksDeleting: false,
  onboardingHabitPacks: [],
  isSendingInstalledHabitPackId: false,
  isSendingInstalledHabitId: null,
  isPackAndFocusEditorOpen: false,
  packEditorTabMode: null,
  editorType: null,
  editorMode: null,
  userSettingsSnapshot: null,
  focusTemplateSnapshot: null,
  initialFocusTemplateSnapshot: null,
  isPackPreviewOpen: false,
  previewType: null,
  previewEntityId: null,
  previewVariant: null
};

export const initialFocusModeState = (): FocusModeSlice => ({
  currentViewedFocusTemplate: null,
  userCreatedFocusTemplates: [],
  marketplaceApprovedFocusTemplates: [],
  areFocusTemplatesLoading: false,
  isUserInstalledFocusTemplatesFetching: true,
  userInstalledFocusTemplates: [],
  isFocusTemplateInstalling: false,
  isFocusTemplateUninstalling: false,
  isFocusTemplateFetching: false,
  sharedFocusTemplate: null,
  isSharedFocusTemplateFetching: true,
  initialCurrentViewedFocusTemplate: null,
  areTagsFetching: false,
  tags: [],
  isMarketFocusTemplatesFetching: false,
  isFocusTemplateDeleting: false
});

export const initialStatsState: StatsState = {
  summaries: [],
  logQuestionSummaries: [],
  isRoutineStatsLoading: false,
  isHeatmapLoading: false,
  consistencyStats: [],
  isConsistencyStatsLoading: false,
  hasConsistencyStatsError: false,
  selectedHabitId: ''
};

export const initialModalState: ModalSlice = {
  moreOption: {
    activity: DEFAULT_MORE_OPTION_ACTIVITY,
    showYouTubeURLModal: false,
    showAllowedURLModal: false,
    showChoicesModal: false,
    showImageURLModal: false,
    areLogQuestionsInvalid: false,
    isMoreOptionEdited: false,
    showCheckListModal: false,
    isNewActivity: false,
    isUseApplicationsChecked: false,
    isUseWebsiteChecked: false,
    isUseFocusModeChecked: false,
    refreshDuration: false,
    activeTab: MORE_OPTION_REVAMP_TAB.TIMING
  },
  choice: {
    choices: [],
    showChoicesYouTubeURLModal: false,
    selectedPosition: 0,
    areLogQuestionsInvalid: false,
    isChoiceEdited: false
  },
  note: {
    text: undefined,
    showNoteModal: false,
    isCurrentActivityPlaying: false
  },
  logQuantity: {
    showLogQuantityModal: false,
    log_quantity_answers: [],
    areLogDataValid: false,
    showQuestionDetailsModal: false
  },
  logQuantityDetails: {
    position: null,
    logQuestion: null
  },
  addToCalendar: {
    showAddToCalendarModalOpened: false,
    activity: null
  },
  focusSummaryGraph: {
    showFocusSummaryGraphModal: false
  },
  eisenhowerMatrix: {
    isEisenhowerMatrixModalOpened: false
  },
  routineActivitiesStats: {
    showRoutineActivitiesStatsModal: false
  },
  toDoSubTasks: DEFAULT_TO_DO_SUBTASKS,
  confirmAction: DEFAULT_CONFIRM_ACTION_MODAL,
  focusSummaryMore: {
    showFocusSummaryMore: false,
    focusSummary: null
  },
  focusSummaryEdit: {
    showFocusSummaryEdit: false,
    focusSummary: null,
    mode: FOCUS_SUMMARY_MODAL_MODE.CREATE
  },
  keywordModal: {
    id: '',
    keyword: '',
    platform: '',
    isKeywordModalOpened: false,
    title: false,
    description: false
  },
  deleteKeywordModal: {
    id: '',
    keyword: '',
    platform: '',
    isDeleteKeywordModalOpened: false
  },
  longTermGoalsModal: {
    isLongTermGoalsModalOpened: false
  },
  cancelSubscriptionModal: {
    isCancelSubscriptionModalOpened: false
  }
};

export const initialAdminState: AdminSlice = {
  users: [],
  focusModes: [],
  userActivities: [],
  skip: 0,
  orderBy: FetchUsersOrderByOptions.ALL,
  searchBy: {
    user_id: EMPTY_STRING,
    stripe_customer_id: EMPTY_STRING,
    email: EMPTY_STRING,
    revenue_cat_status: EMPTY_STRING,
    updated_at: EMPTY_STRING
  },
  isLoading: true,
  isFetchingActivities: false,
  isFetchingFocusModes: false,
  userActivitiesFilter: ActivityTableFilter.ALL,
  activityOrFocusModeSearchResult: SEARCH_RESULT.NONE,
  showRequestDetailsModal: false,
  accessRequestedUserId: EMPTY_STRING,
  isAccessRequesting: false,
  isAccessRequestGranted: false,
  isFetchingUserDetails: false,
  isSearchByValid: {
    user_id: false,
    stripe_customer_id: false,
    email: false
  },
  isDeviceFetching: false,
  isCreatingSurvey: false,
  isUpdatingSurvey: false,
  isFetchingSurveys: false,
  isFetchingTasks: false,
  isExtendingTrial: false,
  isCancelingSubscription: false,
  isUpgradingPlan: false,
  userTasks: [],
  showSurveyDetailModal: false,
  surveys: [],
  selectedSurvey: null,
  announcements: [],
  selectedAnnouncement: null,
  announcementsMeta: null,
  isFetchingAnnouncements: false,
  isCreatingAnnouncement: false,
  isUpdatingAnnouncement: false,
  isDeletingAnnouncement: false
};

export const initialAdminTeamsState: AdminTeamsSlice = {
  teams: [],
  totalCount: 0,
  areTeamsLoading: false,
  isSavingTeam: false,
  selectedTeam: null,
  showTeamFormModal: false
};

export const initialChatBotState: ChatBotSlice = {
  message: EMPTY_STRING,
  chatHistory: [
    {
      role: ChatRoles.ASSISTANT,
      content: t('chatbot.introduction')
    }
  ],
  aiGeneratedContent: null,
  chatBotState: ChatBotState.IDLE,
  isChatBotGenerating: false
};

export const initialFilterState: FilterSlice = {
  searchWord: EMPTY_STRING,
  priority: EMPTY_STRING,
  daysOfWeek: [],
  isSettingToolbarShown: false,
  isAdminToolbarShown: true
};

export const initialInquiryState: InquirySlice = {
  is_inquiry_modal_shown: false,
  installation_path: {
    desktop: true,
    mobile: false
  },
  reminder: {
    desktop_app: true,
    calendar: false
  }
};

export const initialGeneralState: GeneralSlice = {
  hasMotivationalDelayElapsed: false,
  apiRateLimitedUntil: null,
  hasDataLoadFailed: false,
  isOffline: false,
  tabLayout: {
    activeTab: DEFAULT_TAB_LAYOUT_TAB
  }
};

export const initialCacheState: CacheSlice = {
  isYouTubeURLMetadataUpdating: false,
  isYouTubeURLMetadataFetching: false,
  youtube_urls_metadata: {
    videos_metadata: []
  }
};

export const initialProcrastinateState: ProcrastinateSlice = {
  website: {
    data: EMPTY_STRING,
    isValid: false
  },
  savedWebsites: [],
  isSavedWebsitesCreating: false,
  isSavedWebsitesFetching: false,
  isSavedWebsitesUpdating: false,
  isSavedWebsitesDeleting: false,
  selectedWebsiteIDs: [],
  isProcrastinateDrawerOpen: false
};

const initialTodoStatusPagination = (): TodoStatusPagination => ({
  [TO_DO_STATUS.IN_PROGRESS]: { page: 0, hasNextPage: false, itemCount: 0 },
  [TO_DO_STATUS.NOT_STARTED]: { page: 0, hasNextPage: false, itemCount: 0 },
  [TO_DO_STATUS.COMPLETED]: { page: 0, hasNextPage: false, itemCount: 0 }
});

const initialTasksByStatus = (): TodoTasksByStatus => ({
  [TO_DO_STATUS.IN_PROGRESS]: [],
  [TO_DO_STATUS.NOT_STARTED]: [],
  [TO_DO_STATUS.COMPLETED]: []
});

export const initialToDoState: ToDoSlice = {
  tasks: [],
  tasksByStatus: initialTasksByStatus(),
  todoStatusPagination: initialTodoStatusPagination(),
  googleCalendarData: {
    calendars: [],
    keywords: []
  },
  microsoftCalendarData: {
    calendars: [],
    keywords: []
  },
  isCreatingToDo: false,
  isFetchingToDo: false,
  isUpdatingToDo: false,
  shouldResetNewTaskForm: false,
  shouldUpdateTextEditorContent: false,
  isTaskUpdating: false,
  updatingTaskIds: {},
  showEmbeddedCreateToDoModal: false,
  isTaskSubmitting: false,
  activeTabIndex: TO_DO_TAB.TO_DO,
  newTask: DEFAULT_NEW_TASK(),
  newTodoPlayerTask: null,
  incomingTasks: [],
  player: DEFAULT_TO_DO_PLAYER,
  showBrainDumpTasksModal: false,
  isConvertingBrianDump: false,
  isAddingConvertedTasksToPlayer: false,
  todoFilters: DEFAULT_FILTER_DATA,
  showNewTaskForm: false,
  selectedTaskToTaskForm: null,
  isDeletingTask: false,
  taskComments: {},
  isFetchingComments: false,
  taskReactions: {},
  isFetchingTaskReactions: false,
  isCreatingComment: false,
  isUpdatingComment: false,
  isDeletingComment: false,
  isAddingReaction: false,
  isRemovingReaction: false,
  isAddingTaskReaction: false,
  isRemovingTaskReaction: false,
  taskAttachments: {},
  isFetchingAttachments: false,
  isUploadingAttachment: false,
  isDeletingAttachment: false,
  commentAttachments: {},
  isFetchingCommentAttachments: false,
  isUploadingCommentAttachment: false,
  isDeletingCommentAttachment: false,
  showVagueInputPrompt: false,
  showFilters: false,
  wheelSource: null
};

export const initialPlatformIntegrationState: PlatformIntegrationSlice = {
  areProjectsLoading: true,
  isProjectSynching: false,
  projects: [],
  areExternalStatusesSyncing: false,
  showPlatformProjects: false,
  statusToCountAsDone: [],
  currentProjectId: EMPTY_STRING,
  currentPlatform: null,
  projectsToBeSynced: [],
  showProjectTasksModal: false,
  projectTasksModal: {
    tasks: []
  },
  isLoadingProjectTasks: true,
  isSynchingProjectTasks: false,
  selectedIntegrationTab: IntegrationTab.PROJECT
};

export const initialTeamState: TeamSlice = {
  teams: [],
  selectedTeam: null,
  areTeamsLoading: false,
  teamsInitialized: false,
  areMembersLoading: false,
  membersLoadedTeamId: null,
  staffUsage: null,
  isStaffUsageLoading: false,
  teamMembers: DEFAULT_TEAM_MEMBERS,
  isInvitingUser: false,
  isAssigningAdmin: false,
  isRemovingAdmin: false,
  isAssigningTeacher: false,
  isRemovingTeacher: false,
  isUpdatingMemberRole: false,
  isRemovingMember: false,
  isUpdatingTeamName: false,
  isUpdatingClassSessionWindow: false,
  isSessionWindowDrawerOpen: false,
  isDeletingTeam: false,
  showTeamOwnerActionModal: false,
  isAcceptingInvitation: false,
  isInvitationAccepted: false,
  isUpdatingExpiryDate: false,
  searchMember: EMPTY_STRING,
  showTeamOwnerMassUpdateModal: false,
  isExpiryDateMassUpdating: false,
  isMemberTypeMassUpdating: false,
  invitationLinkHasCustomError: {
    hasError: false,
    message: ''
  },
  isAddingMemberManually: false,
  memberDetailsModal: {
    visible: false
  },
  columnToSortBy: TEAM_COLUMN_TITLES.EMAIL,
  joinCodes: [],
  areJoinCodesLoading: false,
  isCreatingJoinCode: false
};

export const initialClassManagementState: ClassManagementSlice = {
  classes: [],
  areClassesLoading: false,
  classesInitialized: false,
  selectedClass: null,
  isCreatingClass: false,
  isRenamingClass: false,
  isArchivingClass: false,
  liveSession: null,
  isLiveSessionLoading: false,
  sharedFocusModes: [],
  areSharedFocusModesLoading: false,
  isSavingAllowlistToTemplate: false,
  allowlistTemplateSavedSessionId: null,
  isStartingSession: false,
  isEndingSession: false,
  isAmendingAllowlist: false,
  isAmendingSessionTasks: false,
  participants: [],
  areParticipantsLoading: false,
  unlockRequests: [],
  areUnlockRequestsLoading: false,
  resolvingUnlockRequestId: null,
  resolvingUnlockStudentId: null,
  releasingStudentId: null,
  reportsBySessionId: {},
  reportLoadingSessionIds: [],
  showStartSessionModal: false,
  canStartSession: true,
  justEndedSessionId: null,
  showStudentsModal: false,
  showSessionHistoryModal: false,
  showClassReportsModal: false,
  classMembers: [],
  classAdminIds: [],
  classTeacherIds: [],
  classStudents: [],
  classTeachers: [],
  areClassMembersLoading: false,
  removingMemberId: null,
  candidateMembers: [],
  areCandidateMembersLoading: false,
  areCandidateMembersRestricted: false,
  isAddingMembers: false,
  sessions: [],
  areSessionsLoading: false,
  attendance: [],
  areAttendanceLoading: false,
  concernCriteria: null
};

export const initialClassSessionState: ClassSessionSlice = {
  invitations: [],
  currentSession: null,
  areInvitationsLoading: false,
  isCurrentSessionLoading: false,
  isJoining: false,
  isLeaving: false,
  isDeclining: false,
  isSubmittingUnlock: false,
  lastHeartbeatAt: null
};

export const initialPaginationState: PaginationSlice = {
  paginationType: null,
  currentPage: PAGE.FIRST,
  totalPages: PAGE.INIT_TOTAL,
  itemsPerPage: PAGE.TAKE.DEFAULT,
  hasPreviousPage: false,
  hasNextPage: false
};

export const initialCourseState: CourseSlice = {
  details: null,
  isLoadingCourseDetails: true,
  isCreatingLessonCompletion: false,
  isFetchingTutorials: false,
  tutorials: [],
  other_courses: [],
  enrolled_courses: [],
  isFetchingOtherCourses: false,
  isFetchingEnrolledCourses: true,
  isCreatingCourseEnrolment: false,
  showCourseDetailsModal: false,
  isUpdatingCourseCompletion: false
};

export const initialRoutineSuggestions: RoutineSuggestionsSlice = {
  isFetchingRoutineSuggestions: false,
  routineSuggestions: {},
  routineSuggestionPayload: DEFAULT_ROUTINE_SUGGESTION_PAYLOAD,
  areSuggestedHabitPacksInstalled: false,
  isInstallingRoutineSuggestions: false,
  showEditSuggestionsModal: false,
  selectedGoals: [],
  availableGoals: DEFAULT_AVAILABLE_GOAL_CATEGORIES,
  confirmSetupExistingHabits: false,
  rawRoutineSuggestions: {},
  isRefiningRoutineHabits: false
};

export const initialSubscriptionState: SubscriptionSlice = {
  stripPlans: [],
  isFetchingStripPlans: false,
  isCheckingSubscription: false,
  subscriptionInfo: {},
  isSubscriptionChecking: false,
  subscriptions: [],
  activeEntitlements: [],
  entitlements: [],
  isCheckoutInProgress: false,
  isPlanChooserDrawerOpen: false,
  isAddingPaymentMethodInProgress: false
};

export const initialAccountabilityBuddyState: AccountabilityBuddySlice = {
  buddies: [],
  invitations: [],
  unlockRequests: [],
  unlockRequestsMeta: null,
  unlockRequestFilters: {
    page: 1,
    take: 10,
    role: undefined,
    created_from: undefined,
    created_to: undefined,
    status: undefined
  },
  isFetchingBuddy: false,
  isFetchingInvitations: false,
  isInviting: false,
  isAcceptingInvitation: false,
  isCreatingUnlockRequest: false,
  isProcessingUnlockRequest: false,
  isFetchingUnlockRequests: false,
  showInviteDrawer: false,
  showCreateUnlockRequestDrawer: false,
  showEndPartnershipConfirm: false,
  endPartnershipBuddyId: null,
  showApproveUnlockRequestConfirm: false,
  showRejectUnlockRequestConfirm: false,
  unlockRequestId: null,
  activeTabIndex: 0,
  invitationLinkHasCustomError: {
    hasError: false,
    message: ''
  }
};

export const initialProjectState: ProjectSlice = {
  projects: [],
  pendingInvitations: [],
  currentProject: null,
  isProjectsLoading: false,
  isLoadingMoreProjects: false,
  isPendingInvitationsLoading: false,
  isProjectLoading: false,
  isCreatingProject: false,
  isUpdatingProject: false,
  isDeletingProject: false,
  isInvitingMember: false,
  isUpdatingMember: false,
  isRemovingMember: false,
  isAcceptingInvitation: false,
  isDecliningInvitation: false,
  isLeavingProject: false,
  showCreateProjectModal: false,
  showEditProjectModal: false,
  showInviteMemberModal: false,
  showProjectDetailsModal: false,
  pagination: null,
  projectDetails: {
    tasks: [],
    isTasksLoading: false,
    tasksError: null,
    mode: ProjectDetailsViewMode.LIST,
    activeTab: ProjectDetailTab.TASKS,
    showFilters: false,
    filters: {
      taskName: '',
      perspirationLevel: null,
      dueDateRange: null,
      sortMode: SORT_MODE.SMART
    },
    showMembersModal: false,
    showSettingsDrawer: false,
    showTaskDrawer: false,
    selectedTaskToEdit: null,
    isCreatingTask: false,
    isUpdatingTask: false,
    isDeletingTask: false,
    isTaskStatusUpdating: false,
    taskStatusUpdatingIds: []
  },
  debouncedSearch: EMPTY_STRING,
  showCapacitySettings: false,
  showPendingInvitationsDrawer: false,
  listViewMode: ProjectsListViewMode.FOCUS,
  listStatusFilter: ProjectLifecycleStatus.ACTIVE
};

export const initialMcpTokenState: McpTokenSlice = {
  tokens: [],
  isFetchingTokens: false,
  isCreatingToken: false,
  isRevokingTokenId: null,
  newlyIssuedToken: null,
  showCreateModal: false,
  showRevokeConfirmId: null,
  mcpAgents: [],
  isMcpAgentsLoading: false
};

export const initialPortfolioState: PortfolioSlice = {
  portfolio: null,
  isLoading: false,
  topPriorityTasksByProjectId: {},
  settings: null,
  isSettingsLoading: false,
  isSavingSettings: false,
  forecast: null,
  isForecastLoading: false
};

export const initialDayPlanState: DayPlanSlice = {
  selectedDay: formatDateToYMD(new Date()),
  tasks: [],
  dismissedEventIds: [],
  eventColors: {},
  calendarEvents: [],
  isLoadingEvents: false,
  isLoadingDayPlan: false,
  isReviewOpen: false,
  fileImportEvents: [],
  fileImportEventSeries: [],
  fileImportUnscheduledEntries: [],
  fileImportTruncated: false,
  isImportingFile: false,
  fileImportCommitProgress: null,
  eventSeriesOccurrences: [],
  isLoadingEventSeries: false,
  dayPlanOneOffEvents: [],
  isLoadingDayPlanOneOffEvents: false,
  taskCatalog: [],
  isLoadingTaskCatalog: false,
  taskCatalogSortMode: SORT_MODE.SMART,
  taskCatalogSearchQuery: '',
  todayPlanSegment: DayPlanTodaySegment.HABITS,
  routineProgress: null
};

export const initialNotesState: NotesState = {
  notes: [],
  selectedNote: null,
  userTags: [],
  isFetchingNotes: false,
  hasLoadedNotes: false,
  isCreatingNote: false,
  isUpdatingNote: false,
  isDeletingNote: false,
  isFetchingTags: false,
  showNoteModal: false,
  showDeleteConfirmModal: false,
  noteToDelete: null,
  hasNextPage: false,
  hasPreviousPage: false,
  currentPage: 1,
  searchQuery: '',
  searchResults: null,
  isSearching: false,
  noteFolders: null,
  hasLoadedNoteFolders: false,
  activeNoteFolderId: null
};

export const initialProjectNoteState: ProjectNoteSlice = {
  notes: [],
  pagination: null,
  isNotesLoading: false,
  isCreatingNote: false,
  currentNote: null,
  isNoteLoading: false,
  isSavingNote: false,
  isDeletingNote: false,
  comments: [],
  isCommentsLoading: false,
  isAddingComment: false
};
